import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Repeat } from 'lucide-react';

const RecurringTaskModal = ({ isOpen, onClose, onSave, initialRule = '' }) => {
  const [recurringConfig, setRecurringConfig] = useState({
    frequency: 'weekly', // daily, weekly, monthly, yearly
    interval: 1, // every X days/weeks/months/years
    daysOfWeek: [], // for weekly: [1,2,3,4,5,6,0] (Mon-Sun)
    monthlyType: 'date', // 'date' or 'day' (15th vs 2nd Monday)
    monthlyDate: 1, // day of month (1-31)
    monthlyWeek: 1, // which week (1st, 2nd, 3rd, 4th, last)
    monthlyDay: 1, // day of week for monthly (1=Mon, 0=Sun)
    startTime: '', // start time of day (empty by default)
    endTime: '', // end time of day (optional)
    endType: 'never', // 'never', 'after', 'by'
    endAfter: 10, // number of occurrences
    endBy: '', // end date
    timezone: 'local',
    // Due date rule (relative to task creation)
    dueDateOffset: 0, // number of time periods
    dueDatePeriod: 'days', // 'days', 'weeks', 'months'
    isRecurringTemplate: false // mark as recurring template
  });

  const [previewText, setPreviewText] = useState('');

  const weekDays = [
    { value: 1, label: 'Mon', shortLabel: 'M' },
    { value: 2, label: 'Tue', shortLabel: 'T' },
    { value: 3, label: 'Wed', shortLabel: 'W' },
    { value: 4, label: 'Thu', shortLabel: 'T' },
    { value: 5, label: 'Fri', shortLabel: 'F' },
    { value: 6, label: 'Sat', shortLabel: 'S' },
    { value: 0, label: 'Sun', shortLabel: 'S' }
  ];

  const monthlyWeeks = [
    { value: 1, label: '1st' },
    { value: 2, label: '2nd' },
    { value: 3, label: '3rd' },
    { value: 4, label: '4th' },
    { value: -1, label: 'Last' }
  ];

  // Parse initial rule if provided
  useEffect(() => {
    console.log('🔄 RecurringTaskModal initialRule changed:', initialRule);
    if (initialRule && initialRule !== 'custom') {
      parseInitialRule(initialRule);
    }
  }, [initialRule]);

  // Generate preview text
  useEffect(() => {
    generatePreviewText();
  }, [recurringConfig]);

  const parseInitialRule = (rule) => {
    if (!rule) return;
    
    console.log('🔍 Parsing initial rule:', rule);
    
    try {
      // Try to parse as JSON first (new format)
      const parsed = JSON.parse(rule);
      console.log('✅ Parsed JSON rule:', parsed);
      
      // Handle backward compatibility: migrate old 'time' field to 'startTime'
      if (parsed.time && !parsed.startTime) {
        parsed.startTime = parsed.time;
        delete parsed.time;
      }
      
      console.log('📝 Setting recurring config with parsed data');
      setRecurringConfig(prev => ({ ...prev, ...parsed }));
    } catch (e) {
      console.error('❌ Failed to parse JSON, trying legacy format:', e);
      // Parse common patterns like 'daily', 'weekly', 'monthly', etc. (old format)
      const patterns = {
        'daily': { frequency: 'daily', interval: 1, daysOfWeek: [] },
        'weekly': { frequency: 'weekly', interval: 1, daysOfWeek: [new Date().getDay() === 0 ? 0 : new Date().getDay()] },
        'biweekly': { frequency: 'weekly', interval: 2, daysOfWeek: [new Date().getDay() === 0 ? 0 : new Date().getDay()] },
        'monthly': { frequency: 'monthly', interval: 1, monthlyType: 'date', monthlyDate: new Date().getDate() },
        'quarterly': { frequency: 'monthly', interval: 3, monthlyType: 'date', monthlyDate: new Date().getDate() },
        'yearly': { frequency: 'yearly', interval: 1 }
      };

      if (patterns[rule]) {
        setRecurringConfig(prev => ({ ...prev, ...patterns[rule] }));
      }
    }
  };

  const generatePreviewText = () => {
    const { frequency, interval, daysOfWeek, monthlyType, monthlyDate, monthlyWeek, monthlyDay, startTime, endTime, endType, endAfter, endBy } = recurringConfig;
    
    let text = 'Repeats ';
    
    // Frequency and interval
    if (frequency === 'daily') {
      if (daysOfWeek.length === 0) {
        // Every day
        text += interval === 1 ? 'daily' : `every ${interval} days`;
      } else if (daysOfWeek.length === 5 && daysOfWeek.every(d => [1,2,3,4,5].includes(d))) {
        // Weekdays only
        text += interval === 1 ? 'on weekdays' : `every ${interval} days on weekdays`;
      } else if (daysOfWeek.length === 2 && daysOfWeek.every(d => [0,6].includes(d))) {
        // Weekends only
        text += interval === 1 ? 'on weekends' : `every ${interval} days on weekends`;
      } else if (daysOfWeek.length === 1) {
        const dayName = weekDays.find(d => d.value === daysOfWeek[0])?.label || 'day';
        text += interval === 1 ? `every ${dayName}` : `every ${interval} days on ${dayName}`;
      } else if (daysOfWeek.length > 1) {
        const dayNames = daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ');
        text += interval === 1 ? `on ${dayNames}` : `every ${interval} days on ${dayNames}`;
      }
    } else if (frequency === 'weekly') {
      if (interval === 1) {
        if (daysOfWeek.length === 1) {
          const dayName = weekDays.find(d => d.value === daysOfWeek[0])?.label || 'day';
          text += `every ${dayName}`;
        } else if (daysOfWeek.length > 1) {
          const dayNames = daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ');
          text += `weekly on ${dayNames}`;
        } else {
          text += 'weekly';
        }
      } else {
        const dayNames = daysOfWeek.length > 0 
          ? ' on ' + daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ')
          : '';
        text += `every ${interval} weeks${dayNames}`;
      }
    } else if (frequency === 'monthly') {
      const monthText = interval === 3 ? 'quarterly' : (interval === 1 ? 'monthly' : `every ${interval} months`);
      if (monthlyType === 'date') {
        const suffix = getOrdinalSuffix(monthlyDate);
        text += `${monthText} on the ${monthlyDate}${suffix}`;
      } else {
        const weekText = monthlyWeeks.find(w => w.value === monthlyWeek)?.label || '1st';
        const dayName = weekDays.find(d => d.value === monthlyDay)?.label || 'Monday';
        text += `${monthText} on the ${weekText} ${dayName}`;
      }
    } else if (frequency === 'yearly') {
      text += interval === 1 ? 'yearly' : `every ${interval} years`;
    }

    // Time
    if (startTime) {
      const startTimeFormatted = formatTime(startTime);
      if (endTime) {
        const endTimeFormatted = formatTime(endTime);
        text += ` from ${startTimeFormatted} to ${endTimeFormatted}`;
      } else {
        text += ` at ${startTimeFormatted}`;
      }
    }

    // End condition
    if (endType === 'after') {
      text += `, ending after ${endAfter} occurrences`;
    } else if (endType === 'by' && endBy) {
      text += `, ending on ${new Date(endBy).toLocaleDateString()}`;
    }

    setPreviewText(text);
  };

  const getOrdinalSuffix = (num) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const value = num % 100;
    return suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour12 = hours % 12 || 12;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const toggleDayOfWeek = (day) => {
    setRecurringConfig(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort()
    }));
  };

  const generateCronExpression = () => {
    // Convert our config to a cron-like expression or JSON that the backend can understand
    const { frequency, interval, daysOfWeek, monthlyType, monthlyDate, monthlyWeek, monthlyDay, startTime, endTime, dueDateOffset, dueDatePeriod } = recurringConfig;
    
    // Automatically set as recurring template when any schedule is configured
    return JSON.stringify({
      frequency,
      interval,
      daysOfWeek,
      monthlyType,
      monthlyDate,
      monthlyWeek,
      monthlyDay,
      startTime,
      endTime,
      endType: recurringConfig.endType,
      endAfter: recurringConfig.endAfter,
      endBy: recurringConfig.endBy,
      dueDateOffset,
      dueDatePeriod,
      isRecurringTemplate: true  // Always true when a recurring schedule is set
    });
  };

  const handleSave = () => {
    const cronExpression = generateCronExpression();
    onSave(cronExpression, previewText);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <Repeat className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Configure Recurring Task</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Frequency Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Repeat</label>
            <div className="grid grid-cols-4 gap-3 mb-3">
              {[
                { value: 'daily', label: 'Daily', icon: '📅' },
                { value: 'weekly', label: 'Weekly', icon: '📊' },
                { value: 'monthly', label: 'Monthly', icon: '🗓️' },
                { value: 'yearly', label: 'Yearly', icon: '🎂' }
              ].map((freq) => (
                <button
                  key={freq.value}
                  onClick={() => setRecurringConfig(prev => ({ ...prev, frequency: freq.value }))}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    recurringConfig.frequency === freq.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">{freq.icon}</div>
                  <div className="text-sm font-medium">{freq.label}</div>
                </button>
              ))}
            </div>
            {/* Quick Selector for Quarterly */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Quick select:</span>
              <button
                type="button"
                onClick={() => setRecurringConfig(prev => ({ 
                  ...prev, 
                  frequency: 'monthly', 
                  interval: 3,
                  monthlyType: 'date',
                  monthlyDate: 1
                }))}
                className={`px-3 py-1 rounded-md border transition-colors text-sm ${
                  recurringConfig.frequency === 'monthly' && recurringConfig.interval === 3
                    ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                📊 Quarterly
              </button>
            </div>
          </div>

          {/* Interval */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Every</label>
              <select
                value={recurringConfig.interval}
                onChange={(e) => setRecurringConfig(prev => ({ ...prev, interval: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {recurringConfig.frequency === 'daily' ? 'Days' :
                 recurringConfig.frequency === 'weekly' ? 'Weeks' :
                 recurringConfig.frequency === 'monthly' ? 'Months' : 'Years'}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                {recurringConfig.frequency}
              </div>
            </div>
          </div>

          {/* Daily and Weekly Configuration - Day Selection */}
          {(recurringConfig.frequency === 'daily' || recurringConfig.frequency === 'weekly') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {recurringConfig.frequency === 'daily' ? 'On days' : 'On days'}
              </label>
              <div className="flex space-x-2">
                {weekDays.map((day) => (
                  <button
                    key={day.value}
                    onClick={() => toggleDayOfWeek(day.value)}
                    className={`w-10 h-10 rounded-full border-2 transition-colors ${
                      recurringConfig.daysOfWeek.includes(day.value)
                        ? 'border-purple-500 bg-purple-500 text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {day.shortLabel}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">
                  {recurringConfig.frequency === 'daily' 
                    ? 'Select which days to repeat (leave empty for every day)' 
                    : 'Select which days of the week'}
                </p>
                {recurringConfig.frequency === 'daily' && (
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setRecurringConfig(prev => ({ ...prev, daysOfWeek: [1,2,3,4,5] }))}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      Weekdays
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecurringConfig(prev => ({ ...prev, daysOfWeek: [0,6] }))}
                      className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                    >
                      Weekends
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecurringConfig(prev => ({ ...prev, daysOfWeek: [] }))}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Every day
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Monthly Configuration */}
          {recurringConfig.frequency === 'monthly' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Monthly repeat type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setRecurringConfig(prev => ({ ...prev, monthlyType: 'date' }))}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      recurringConfig.monthlyType === 'date'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">On date</div>
                    <div className="text-sm text-gray-600">15th of month</div>
                  </button>
                  <button
                    onClick={() => setRecurringConfig(prev => ({ ...prev, monthlyType: 'day' }))}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      recurringConfig.monthlyType === 'day'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">On day</div>
                    <div className="text-sm text-gray-600">2nd Monday</div>
                  </button>
                </div>
              </div>

              {recurringConfig.monthlyType === 'date' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Day of month</label>
                  <select
                    value={recurringConfig.monthlyDate}
                    onChange={(e) => setRecurringConfig(prev => ({ ...prev, monthlyDate: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {recurringConfig.monthlyType === 'day' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Week of month</label>
                    <select
                      value={recurringConfig.monthlyWeek}
                      onChange={(e) => setRecurringConfig(prev => ({ ...prev, monthlyWeek: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {monthlyWeeks.map(week => (
                        <option key={week.value} value={week.value}>{week.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Day of week</label>
                    <select
                      value={recurringConfig.monthlyDay}
                      onChange={(e) => setRecurringConfig(prev => ({ ...prev, monthlyDay: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {weekDays.map(day => (
                        <option key={day.value} value={day.value}>{day.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Time Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Schedule</label>
            <div className="space-y-3">
              {/* Start Time */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Time (Optional)</label>
                <div className="flex items-center space-x-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <input
                    type="time"
                    value={recurringConfig.startTime}
                    onChange={(e) => setRecurringConfig(prev => ({ ...prev, startTime: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {recurringConfig.startTime && (
                    <span className="text-sm text-gray-500">
                      ({formatTime(recurringConfig.startTime)})
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for all-day flexible scheduling
                </p>
              </div>

              {/* End Time */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Time (Optional)</label>
                <div className="flex items-center space-x-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <input
                    type="time"
                    value={recurringConfig.endTime}
                    onChange={(e) => setRecurringConfig(prev => ({ ...prev, endTime: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder=""
                    disabled={!recurringConfig.startTime}
                  />
                  {recurringConfig.endTime && (
                    <span className="text-sm text-gray-500">
                      ({formatTime(recurringConfig.endTime)})
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {recurringConfig.startTime 
                    ? "End time creates a specific time window"
                    : "Requires start time to be set first"
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Due Date Rule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date Rule</label>
            <p className="text-xs text-gray-500 mb-3">Set when generated tasks should be due (relative to creation date)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={recurringConfig.dueDateOffset}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, dueDateOffset: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
                <select
                  value={recurringConfig.dueDatePeriod}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, dueDatePeriod: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="days">Days after creation</option>
                  <option value="weeks">Weeks after creation</option>
                  <option value="months">Months after creation</option>
                </select>
              </div>
            </div>
            <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
              {recurringConfig.dueDateOffset === 0 
                ? "Tasks will be due on the same day they're created"
                : `Tasks will be due ${recurringConfig.dueDateOffset} ${recurringConfig.dueDatePeriod} after creation`
              }
            </div>
          </div>

          {/* Info about recurring template behavior */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg">
              <span className="text-lg">🔄</span>
              <div>
                <span className="text-sm font-medium text-purple-900">Recurring Template</span>
                <p className="text-xs text-purple-700 mt-0.5">
                  This task will become a template that automatically generates new tasks on schedule. The template stays active and never completes.
                </p>
              </div>
            </div>
          </div>

          {/* End Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Ends</label>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="endType"
                  value="never"
                  checked={recurringConfig.endType === 'never'}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, endType: e.target.value }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <span className="text-sm">Never</span>
              </label>
              
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="endType"
                  value="after"
                  checked={recurringConfig.endType === 'after'}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, endType: e.target.value }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <span className="text-sm">After</span>
                <input
                  type="number"
                  value={recurringConfig.endAfter}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, endAfter: parseInt(e.target.value) || 1 }))}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                  disabled={recurringConfig.endType !== 'after'}
                />
                <span className="text-sm">occurrences</span>
              </label>
              
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="endType"
                  value="by"
                  checked={recurringConfig.endType === 'by'}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, endType: e.target.value }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <span className="text-sm">On</span>
                <input
                  type="date"
                  value={recurringConfig.endBy}
                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, endBy: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={recurringConfig.endType !== 'by'}
                />
              </label>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Preview</span>
            </div>
            <p className="text-sm text-blue-800">{previewText}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Save Recurring Rule
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecurringTaskModal;